
import React, { useEffect, useState } from 'react';
import type { User } from '../../../types';
import { authService } from '../../../services';
import arrowDropDownIcon from '../../../assets/arrow_drop_down.svg';
import NameWithEmailTooltip from '../../../components/ui/NameWithEmailTooltip';


const ProfilePage: React.FC = () => {
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const email = await authService.getStoredUser()?.email ?? '';
        const response = await authService.getCurrentUser(email);
        if (response !== null) {
          // Cast to unknown first, then to ApiProfileData to avoid type error
          setProfile(response.data as unknown as User);
        } else {
          setError('User not found, 404');
        }
      } catch (error) {
        console.error(error);
        setError('Failed to fetch profile');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  return (
    <div className="w-full min-h-screen px-0">
      <div className="w-full max-w-full bg-white rounded-lg p-0 mx-auto">
        {/* Employee Information Section */}
        <div className="  pb-6">
          <div className="text-primary-800 text-base font-normal font-verdana mb-4 bg-purple-100 p-2 pl-2 rounded">Employee Information</div>
          {loading ? (
            <div className="text-center text-gray-500">Loading...</div>
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : profile ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Employee ID</div>
                <div className="w-1/2 break-words text-sm font-normal text-gray-800">{profile.employee_id?.trim() || '-'}</div>
              </div>
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Name of Employee</div>
                {(profile.name_of_employee || profile.name)?.trim() ? (
                  <NameWithEmailTooltip
                    as="div"
                    className="w-1/2 break-words text-sm font-normal text-gray-800"
                    name={profile.name_of_employee || profile.name}
                    email={profile.email}
                  />
                ) : (
                  <div className="text-sm text-gray-800 font-normal w-1/2">-</div>
                )}
              </div>
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Designation</div>
                <div className="w-1/2 break-words text-sm font-normal text-gray-800">{profile.designation?.trim() || '-'}</div>
              </div>
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Email</div>
                <div className="w-1/2 break-all text-sm font-normal text-gray-800">{profile.email?.trim() || '-'}</div>
              </div>
            
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Department</div>
                <div className="w-1/2 break-words text-sm font-normal text-gray-800">{profile.department?.trim() || '-'}</div>
              </div>
              <div className='flex min-w-0'>
                <div className="text-sm text-gray-500 mb-1 w-1/2 pl-3">Reporting Manager</div>
                {profile.reporting_manager?.trim() ? (
                  <NameWithEmailTooltip
                    as="div"
                    className="w-1/2 break-words text-sm font-normal text-gray-800"
                    name={profile.reporting_manager}
                    email={profile.reporting_manager_email}
                  />
                ) : (
                  <div className="text-sm text-gray-800 font-normal w-1/2">-</div>
                )}
              </div>
            </div>
          ) : null}
        </div>

        {/* Language Preferences Section */}
        <div className=" py-6">
          <div className="text-primary-800 text-base font-normal font-verdana mb-4 bg-purple-100 p-2 pl-2 rounded">Language Preferences</div>
          <div>
            <div className="relative w-full pl-2 sm:w-1/2 group">
              <select
                className="w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-200 cursor-not-allowed appearance-none"
                value={profile?.language_preference || ''}
                disabled
              >
                <option value="en">English</option>
                <option value="hi">Hindi</option>
                <option value="es">Spanish</option>
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                <img src={arrowDropDownIcon} alt="dropdown arrow" className="w-3 h-3" />
              </span>
              {/* Custom Tooltip */}
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-[60] w-max -translate-x-1/2 translate-y-1 rounded-md bg-gray-900 px-3 py-2 text-sm text-white opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100"
              >
                This option will be available to edit in future updates
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></span>
              </span>
            </div>
          </div>
        </div>

        {/* Timezone Section */}
        <div className=" py-6">
          <div className="text-primary-800 text-base font-normal font-verdana mb-4 bg-purple-100 p-2 pl-2 rounded">TimeZone</div>
          <div>
            <div className="relative w-full pl-2 sm:w-1/2 group">
              <select
                className="text-base w-full border border-gray-300 rounded px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-purple-500 bg-gray-200 cursor-not-allowed appearance-none"
                value={profile?.timezone?.split(" ")[0] || ''}
                disabled
              >
                <option value={profile?.timezone?.split(" ")[0] || ''}>{profile?.timezone?.split(" ")[0] || 'Not specified'}</option>
          
              </select>
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-400">
                <img src={arrowDropDownIcon} alt="dropdown arrow" className="w-3 h-3" />
              </span>
              {/* Custom Tooltip */}
              <span
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-[60] w-max -translate-x-1/2 translate-y-1 rounded-md bg-gray-900 px-3 py-2 text-sm text-white opacity-0 shadow-xl transition-opacity duration-200 group-hover:opacity-100"
              >
                This option will be available to edit in future updates
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900"></span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
